'use client';

import type { ChoiceType } from '~/model/ChoiceType';
import { VpIcon, VpIconButton } from '@vtmn-play/react/headless';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import Lightbox from 'react-image-lightbox';
import { ResizedImg } from '~/components/commons/ResizedImg/ResizedImg';
import { apiTypes } from '~/const/api';

export const ChoicesResult = ({
  resultId,
  resultData = [],
  type,
  answerChoices,
}: ChoicesResultPropsType) => {
  const t = useTranslations();
  const [isZoomOpened, setIsZoomOpened] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  const sortedData = [...resultData].sort(
    (a, b) => b.percentage - a.percentage,
  );
  const valueUnit
    = type === apiTypes.CHECK
      ? (
          <span className="results__icon">
            <VpIcon name="check" size={48} />
          </span>
        )
      : (
          '%'
        );
  const resultsWithPicture = sortedData.filter(r => r.media_url);
  const hasPictures = resultsWithPicture.length > 0;

  const handleOpenZoom = (url: string) => {
    setPhotoIndex(resultsWithPicture.findIndex(r => r.media_url === url));
    setIsZoomOpened(true);
  };

  const sortedDataLengthCheck
    = sortedData.length > 0 ? `results__items--by-${sortedData.length}` : '';

  return (
    <div
      data-cy="resultsGroup"
      data-testid="choices"
      className={`results__items ${sortedDataLengthCheck}`}
    >
      {isZoomOpened && hasPictures && (
        <Lightbox
          mainSrc={resultsWithPicture[photoIndex].media_url}
          nextSrc={
            resultsWithPicture[(photoIndex + 1) % resultsWithPicture.length]
              .media_url
          }
          prevSrc={
            resultsWithPicture[
              (photoIndex + resultsWithPicture.length - 1)
              % resultsWithPicture.length
            ].media_url
          }
          onCloseRequest={() => setIsZoomOpened(false)}
          onMovePrevRequest={() => {
            setPhotoIndex(
              (photoIndex + resultsWithPicture.length - 1)
              % resultsWithPicture.length,
            );
          }}
          onMoveNextRequest={() => {
            setPhotoIndex((photoIndex + 1) % resultsWithPicture.length);
          }}
        />
      )}
      {sortedData.map((item: Data, index: number) => {
        const { percentage, media_url } = item;
        const choice
          = item.choice === 'other_responses'
            ? t('SURVEY.RESULTS.OTHER_ANSWER')
            : answerChoices?.length > 0
              && answerChoices.find(q => q['@id'] === item['@id']).title;
        const roundedValue = percentage && Math.round(percentage);
        return (
          <div
            key={`choice-${resultId}-${index}`}
            data-testid="choice"
            className={`results__item ${
              !media_url ? 'results__item--empty' : ''
            }`}
          >
            <div
              data-cy="result"
              data-testid="choice-value"
              className="results__value"
            >
              {roundedValue}
              {' '}
              {valueUnit}
            </div>
            {hasPictures && (
              <div className="results__picture">
                {media_url
                  ? (
                      <>
                        <ResizedImg
                          data-testid="choice-picture"
                          src={media_url}
                          fill
                          format="220x0"
                          sizes="220px"
                          style={{ objectFit: 'contain' }}
                          alt=""
                          onClick={() => handleOpenZoom(media_url)}
                        />
                        <div className="results__picture-zoom">
                          <VpIconButton
                            size="small"
                            variant="secondary"
                            data-testid="picture-zoom"
                            onClick={() => handleOpenZoom(media_url)}
                            title={t('GLOBAL.PICTURE_ZOOM')}
                            tabIndex={-1}
                          >
                            <VpIcon name="eye" />
                          </VpIconButton>
                        </div>
                      </>
                    )
                  : (
                      <span data-testid="choice-picture-span" />
                    )}
              </div>
            )}
            <div className="results__label" data-testid="choice-label">
              {choice}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export interface ChoicesResultPropsType {
  resultId: number;
  resultData: Data[];
  type: string;
  answerChoices: ChoiceType[];
}

interface Data {
  '@id'?: string;
  answers_count: number;
  choice: string;
  percentage: number;
  media_url: string;
}
